import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, FilePlus, ShieldCheck } from "lucide-react";

export function InspectionsList() {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    request_id: "", inspection_type: "on_site", inspection_date: "",
    part_condition: "", material_estimate: "", dimensions_measured: "",
    tolerance_required: "", recommended_method: "", recommended_material: "",
    repair_possible: false, sample_collected: false, notes: "",
  });

  const load = () => {
    api.get("/inspections").then((r) => setItems(r.data.inspections || []));
    api.get("/requests").then((r) => setRequests(r.data.requests || []));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/inspections", { ...f, file_ids: [] });
      toast.success("تم إضافة تقرير الفحص");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">الفحوصات</div>
          <h1 className="text-3xl font-bold text-brand-navy">تقارير الفحص الفني</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta" data-testid="open-inspection-dialog-btn"><FilePlus className="w-4 h-4 me-2" /> تقرير جديد</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>تقرير فحص فني جديد</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>الطلب</Label>
                <Select value={f.request_id} onValueChange={(v) => setF({ ...f, request_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="insp-request-select"><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent>{requests.map((r) => <SelectItem key={r.id} value={r.id}>{r.request_number} - {r.part_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>نوع الفحص</Label>
                <Select value={f.inspection_type} onValueChange={(v) => setF({ ...f, inspection_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="insp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_site">زيارة موقع</SelectItem>
                    <SelectItem value="remote">فحص بيانات</SelectItem>
                    <SelectItem value="sample">فحص عينة</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>تاريخ الفحص</Label><Input type="date" value={f.inspection_date} onChange={(e) => setF({ ...f, inspection_date: e.target.value })} className="mt-1.5" data-testid="insp-date" /></div>
              <div className="col-span-2"><Label>حالة القطعة</Label><Input value={f.part_condition} onChange={(e) => setF({ ...f, part_condition: e.target.value })} className="mt-1.5" data-testid="insp-condition" /></div>
              <div><Label>المادة المقدرة</Label><Input value={f.material_estimate} onChange={(e) => setF({ ...f, material_estimate: e.target.value })} className="mt-1.5" data-testid="insp-material" /></div>
              <div><Label>الأبعاد المقاسة</Label><Input value={f.dimensions_measured} onChange={(e) => setF({ ...f, dimensions_measured: e.target.value })} className="mt-1.5" data-testid="insp-dims" /></div>
              <div><Label>التفاوت المطلوب</Label><Input value={f.tolerance_required} onChange={(e) => setF({ ...f, tolerance_required: e.target.value })} className="mt-1.5" data-testid="insp-tol" /></div>
              <div><Label>طريقة التصنيع المقترحة</Label><Input value={f.recommended_method} onChange={(e) => setF({ ...f, recommended_method: e.target.value })} className="mt-1.5" data-testid="insp-method" /></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox checked={f.repair_possible} onCheckedChange={(v) => setF({ ...f, repair_possible: v })} data-testid="insp-repair" /><Label>الإصلاح ممكن</Label></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox checked={f.sample_collected} onCheckedChange={(v) => setF({ ...f, sample_collected: v })} data-testid="insp-sample" /><Label>تم أخذ عينة</Label></div>
              <div className="col-span-2"><Label>ملاحظات</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" data-testid="insp-notes" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
              <Button className="btn-cta" onClick={submit} data-testid="submit-inspection-btn">حفظ التقرير</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3" data-testid="inspections-list">
        {items.map((i) => (
          <div key={i.id} className="card-industrial p-5" data-testid={`inspection-${i.report_number}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardCheck className="w-5 h-5 text-brand-amber" />
                  <div className="font-bold text-brand-navy">{i.report_number}</div>
                </div>
                <div className="text-sm text-slate-600 mb-1">تاريخ الفحص: {i.inspection_date?.slice(0, 10)}</div>
                <div className="text-sm text-slate-600">حالة القطعة: {i.part_condition || "—"}</div>
              </div>
              <StatusBadge status={i.status} />
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">لا توجد تقارير فحص بعد.</div>}
      </div>
    </DashboardLayout>
  );
}

const QUALITY_STATUS_MAP = {
  approved: "approved",
  rejected: "rejected",
};
const mapQualityStatus = (result) => QUALITY_STATUS_MAP[result] || "needs_review";

export function QualityReportsList() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    order_id: "", inspection_date: "",
    quantity_inspected: 0, quantity_accepted: 0, quantity_rejected: 0,
    dimensions_check: "مطابق", material_certificate: "متوفر", surface_finish: "مطابق",
    tolerance_compliance: "مطابق", overall_result: "approved", notes: "",
  });

  const load = () => {
    api.get("/quality-reports").then((r) => setItems(r.data.reports || []));
    api.get("/orders").then((r) => setOrders(r.data.orders || []));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/quality-reports", {
        ...f,
        quantity_inspected: Number(f.quantity_inspected),
        quantity_accepted: Number(f.quantity_accepted),
        quantity_rejected: Number(f.quantity_rejected),
      });
      toast.success("تم إضافة تقرير الجودة");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">فحوصات الجودة</div>
          <h1 className="text-3xl font-bold text-brand-navy">تقارير الجودة</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta" data-testid="open-quality-dialog-btn"><FilePlus className="w-4 h-4 me-2" /> تقرير جودة</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>تقرير فحص جودة جديد</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>الطلبية</Label>
                <Select value={f.order_id} onValueChange={(v) => setF({ ...f, order_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="qc-order-select"><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>تاريخ الفحص</Label><Input type="date" value={f.inspection_date} onChange={(e) => setF({ ...f, inspection_date: e.target.value })} className="mt-1.5" data-testid="qc-date" /></div>
              <div><Label>الكمية المفحوصة</Label><Input type="number" value={f.quantity_inspected} onChange={(e) => setF({ ...f, quantity_inspected: e.target.value })} className="mt-1.5" data-testid="qc-qty-inspected" /></div>
              <div><Label>الكمية المقبولة</Label><Input type="number" value={f.quantity_accepted} onChange={(e) => setF({ ...f, quantity_accepted: e.target.value })} className="mt-1.5" data-testid="qc-qty-accepted" /></div>
              <div><Label>الكمية المرفوضة</Label><Input type="number" value={f.quantity_rejected} onChange={(e) => setF({ ...f, quantity_rejected: e.target.value })} className="mt-1.5" data-testid="qc-qty-rejected" /></div>
              <div className="col-span-2">
                <Label>النتيجة</Label>
                <Select value={f.overall_result} onValueChange={(v) => setF({ ...f, overall_result: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="qc-result"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">معتمد</SelectItem>
                    <SelectItem value="conditionally_approved">معتمد مشروط</SelectItem>
                    <SelectItem value="rejected">مرفوض</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>ملاحظات</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" data-testid="qc-notes" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
              <Button className="btn-cta" onClick={submit} data-testid="submit-quality-btn">حفظ التقرير</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3" data-testid="quality-reports-list">
        {items.map((i) => (
          <div key={i.id} className="card-industrial p-5" data-testid={`quality-${i.report_number}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <div className="font-bold text-brand-navy">{i.report_number}</div>
                </div>
                <div className="text-sm text-slate-600">تم فحص: {i.quantity_inspected} • قبول: {i.quantity_accepted} • رفض: {i.quantity_rejected}</div>
              </div>
              <StatusBadge status={mapQualityStatus(i.overall_result)} />
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">لا توجد تقارير جودة بعد.</div>}
      </div>
    </DashboardLayout>
  );
}
